import { inject, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { AuthGuard, canActivateRoleGuard } from '@coin/shared/auth/feature';
import { AuthGmsClientCallbackComponent } from '@coin/shared/auth/feature';
import { EnvironmentGuard } from '@coin/shared/core/feature-guards';
import { NoBackendScreenComponent } from '@coin/shared/core/feature-legacy-components';
import { FrontendType, PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { Admin } from '@coin/shared/core/util-helpers';

const routes: Routes = [
  {
    path: 'auth/callback',
    component: AuthGmsClientCallbackComponent
  },
  {
    path: '',
    loadChildren: () => import('./home/home.module').then(m => m.HomePageModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state)
    ],
    data: {
      skipFrontendTypeCheckForRoles: [
        PermissionResource.SuccessionManagementAdmin,
        PermissionResource.OrganisationManagementAdmin,
        PermissionResource.OrganisationPlanningAdmin,
        PermissionResource.HeadcountApprovalAdmin,
        PermissionResource.ApprovalWorkflowsAdmin,
        PermissionResource.JobAdTemplateAdmin
      ],
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'logout',
    loadChildren: () => import('./log-out/log-out.module').then(m => m.LogOutModule)
  },
  {
    path: 'pdfcreate',
    loadChildren: () => import('./pdf-create/pdf-create.module').then(m => m.PdfCreateEmbeddedModule)
  },
  {
    path: Admin.segment('SeasonMgmtModule'),
    loadChildren: () => import('./season-mgmt/season-mgmt.module').then(m => m.SeasonMgmtModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.Seasons
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('TranslationsModule'),
    loadChildren: () => import('./translations/translations.module').then(m => m.TranslationsModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.Translations
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('CmsModule'),
    loadChildren: () => import('./cms/cms.module').then(m => m.CmsModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        },
        {
          resource: PermissionResource.Templates
        },
        {
          resource: PermissionResource.CMS
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('MasterDataModule'),
    loadChildren: () => import('./master-data/master-data.module').then(m => m.MasterDataModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.GeneralCluster
        },
        {
          resource: PermissionResource.Mercer
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'communication',
    loadChildren: () => import('./communication/communication.module').then(m => m.CommunicationModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.Communication
        },
        {
          resource: PermissionResource.Templates,
          actions: [PermissionAction.All]
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'user-mgmt',
    loadChildren: () => import('./user-mgmt/user-mgmt.module').then(m => m.UserMgmtModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.UserManagement
        },
        {
          resource: PermissionResource.RoleManagement
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('IncentiveModule'),
    loadChildren: () => import('./incentive/incentive.module').then(m => m.IncentiveModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.KPIList
        },
        {
          resource: PermissionResource.KPIValues
        },
        {
          resource: PermissionResource.Factors
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'org-planning',
    loadChildren: () => import('./org-planning/org-planning.module').then(m => m.OrgPlanningModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationPlanningAdmin
        }
      ])
    ],
    data: {
      skipFrontendTypeCheckForRoles: [PermissionResource.OrganisationPlanningAdmin],
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('OrgManagementModule'),
    loadChildren: () => import('./org-management/org-management.module').then(m => m.OrgManagementModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationManagementAdmin
        }
      ])
    ],
    data: {
      featureFlag: 'orgManagement',
      frontendType: FrontendType.admin,
      skipFrontendTypeCheckForRoles: [PermissionResource.OrganisationManagementAdmin]
    }
  },
  {
    path: Admin.segment('CosmosModule'),
    loadChildren: () => import('./cosmos/cosmos.module').then(m => m.CosmosModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.CosmosAdmin
        },
        {
          resource: PermissionResource.OrganisationTags
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('EmploymentConditionsLibraryModule'),
    loadChildren: () =>
      import('./employment-conditions-library/employment-conditions-library.module').then(
        m => m.EmploymentConditionsLibraryModule
      ),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.EmploymentConditionLibraryAdmin
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryRestricted
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryGenerator
        },
        {
          resource: PermissionResource.SignatureAuthorizationAdmin
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('RestructuringModule'),
    loadChildren: () => import('./restructuring/restructuring.module').then(m => m.RestructuringModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.RestructuringAdmin
        },
        {
          resource: PermissionResource.RestructuringAdminRestricted
        }
      ])
    ],
    data: {
      featureFlag: 'restructuring',
      frontendType: FrontendType.admin
    }
  },
  {
    path: Admin.segment('SuccessionManagementModule'),
    loadChildren: () =>
      import('./succession-management/succession-management.module').then(m => m.SuccessionManagementModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.SuccessionManagementAdmin
        }
      ])
    ],
    data: {
      skipFrontendTypeCheckForRoles: [PermissionResource.SuccessionManagementAdmin],
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'interfaces',
    loadChildren: () => import('./interfaces/interfaces.module').then(m => m.InterfacesModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'headcount',
    loadChildren: () => import('./headcount/headcount.module').then(m => m.HeadcountModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        },
        {
          resource: PermissionResource.HeadcountApprovalAdmin
        },
        {
          resource: PermissionResource.ApprovalWorkflowsAdmin
        },
        {
          resource: PermissionResource.JobAdTemplateAdmin
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin,
      skipFrontendTypeCheckForRoles: [
        PermissionResource.HeadcountApprovalAdmin,
        PermissionResource.ApprovalWorkflowsAdmin,
        PermissionResource.JobAdTemplateAdmin
      ]
    }
  },
  {
    path: 'api-tests',
    loadChildren: () => import('./api-test-page/api-test-page.module').then(m => m.ApiTestPageModule),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        }
      ])
    ],
    canMatch: [(route: ActivatedRouteSnapshot) => inject(EnvironmentGuard).canMatch(route)],
    data: {
      featureFlag: 'apiTestPage',
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'unauthorized',
    loadChildren: () => import('./unauthorized/unauthorized.module').then(m => m.UnauthorizedModule)
  },
  {
    path: 'fallback',
    component: NoBackendScreenComponent
  },
  {
    path: '**',
    loadChildren: () => import('./not-found/not-found.module').then(m => m.NotFoundModule)
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
